import {CesiumAdapterService} from "../cesiumAdapter21/cesium-adapter.service";
import {ChildOverlayCollectionInterface} from "./ChildOverlayCollectionInterface";
import {OverlayCollectionFactoryInterface} from "./OverlayCollectionFactoryInterface";
import {BehaviorSubject, Subject, timer} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {HttpServiceService} from "../httpService/http-service.service";
import proj4 from "proj4";

export class OverlayType2 {

  private currentZoomLevel = null;

  public isVisible: boolean = true;

  public compositeCollection;
  public sourceCollection = [];

  private _onOverlayDrawed = new Subject<any>();
  get onOverlayDrawed(){
    return this._onOverlayDrawed.asObservable();
  }

  public status:string = 'active';
  public name:string = '';
  public geo_json_url:string = '';
  public id:number = -1;
  public min_zoom:number = 0;
  public max_zoom:number = 0;

  private iteration = 0;

  /**
   * Link tile URL to their entity ID in view
   */
  private entityIds:Map<string, any> = new Map();

  public isDrawn = false;
  public _isLoading = new BehaviorSubject<boolean>(false);

  get isLoading() {
    return this._isLoading.asObservable();
  }

  constructor(
    overlayConfig:Object,
    private view:CesiumAdapterService,
    private httpService: HttpServiceService
  ) {
    this.initializeFromObject(overlayConfig);
    this.compositeCollection = new Cesium.CompositeEntityCollection();
    // this.opacity = 0;
  }

  public getId() { return this.id; }
  public isActive() { return (this.status == 'active'); }

  /**
   * Overlay may be drawn only once
   */
  public draw() {
    if (this.isDrawn) {
      throw new Error("Overlay.draw() may be called only once.");
    }
    this.isDrawn = true;
    // listen zoom changes
    this.view.zoom.pipe(debounceTime(1000)).subscribe((level) => {
      console.log('zoom callback', level);
      this.currentZoomLevel = level;
      this.isVisibleInZoom(level) ? this.show() : this.hide();
    });
  }

  public hide() {
    if(!this.view.viewer){
      return timer(500).subscribe(()=>{
        this.hide();
      })
    }
    this.sourceCollection.forEach(c => {
      this.view.viewer.dataSources.remove(c);
    });
    if (this.view.viewer && this.view.viewer.scene){
      this.view.viewer.scene.requestRender();
      console.log("HIDE");
    }
  }

  public show() {
    this.compositeCollection.show = true;
    if (this.sourceCollection.length > 0) {
      this.sourceCollection.forEach(c => {
        this.view.viewer.dataSources.add(c);
      });
      return;
    }
    this._isLoading.next(true);
    const color = Cesium.Color.fromRandom({
      minimumRed : 0.75,
      minimumGreen : 0.75,
      minimumBlue : 0.75,
      alpha : 1.0
    });
    this.requestSetOfShapes(color, () => {
      this._isLoading.next(false);
      this.view.viewer.scene.requestRender();
    });
  }

  public requestSetOfShapes(color, cb) {
    const url = '/api/get-overlay-type2-shapes/' + window['EVENT_ID'] + '?limit=1000&oid=' + this.getId() + '&offset=' + (this.iteration * 1000);
    this.httpService.sendOutsideGetRequest(url).subscribe(resp => {
      console.log('response', resp);
      this.iteration++;
      if (this.iteration < 100 && resp.items.length > 0) {
        const response = resp.items.filter(item => {
          if (!item) { return false; }
          if (!item.geometry) { return false; }
          // if (item.geometry.type === 'PointM') {
          //   return false;
          // }
          return true;
        });
        response.forEach(item => {
          if (!item) { return; }
          item.properties = {}
          if (item.geometry.type === 'PointM') {
            item.geometry.type = 'Point';
          }
          proj4.defs("S1",resp.prjcs);
          const conv = proj4(proj4.defs('S1'), proj4.defs('WGS84'));
          window['conv'] = conv;
          if (Array.isArray(item.geometry.coordinates[0])) {
            for (let i = 0; i < item.geometry.coordinates.length; i++) {
              if (Array.isArray(item.geometry.coordinates[i][0])) {
                for (let j = 0; j < item.geometry.coordinates[i].length; j++) {
                  if (Array.isArray(item.geometry.coordinates[i][j][0])) {
                    for (let k = 0; k < item.geometry.coordinates[i][j].length; k++) {
                      item.geometry.coordinates[i][j][k] = conv.forward(item.geometry.coordinates[i][j][k]);
                    }
                  } else {
                    item.geometry.coordinates[i][j] = conv.forward(item.geometry.coordinates[i][j]);
                  }
                }
              } else {
                item.geometry.coordinates[i] = conv.forward(item.geometry.coordinates[i]);
              }
            }
          } else {
            item.geometry.coordinates = conv.forward(item.geometry.coordinates.slice(0, 2));
          }
          if (item.geometry.hasOwnProperty('bbox')) {
            const bbox1 = conv.forward([item.geometry.bbox[0], item.geometry.bbox[1]]);
            const bbox2 = conv.forward([item.geometry.bbox[2], item.geometry.bbox[3]]);
            item.geometry.bbox = [bbox1[0], bbox1[1], bbox2[0], bbox2[1]];
          }
        });
        const featureCollection = { type: 'FeatureCollection', features: response };
        Cesium.GeoJsonDataSource.load(featureCollection, {
          stroke: color,
          fill: color,
          strokeWidth: 3,
          markerSymbol: '?',
          clampToGround: true
        }).then(geoJsonDataSource => {
          console.log('geoJsonDataSource', geoJsonDataSource);
          this.view.viewer.dataSources.add(geoJsonDataSource);
          this.sourceCollection.push(geoJsonDataSource);
          console.log("Loaded " + ((this.iteration - 1) * 1000 + response.length) + " records");
          this.requestSetOfShapes(color, cb);
        });

      } else {
        cb();
      }
    });
  }

  /**
   * Copy properties from standard object to this class
   * @param overlayConfig
   */
  protected initializeFromObject(overlayConfig:Object) {
    console.log('overlayConfig', overlayConfig);
    if (overlayConfig) {
      for (let i in overlayConfig) {
        console.log('overlayConfig', i);
        if (overlayConfig.hasOwnProperty(i)) {
          if (this.hasOwnProperty(i)) {
            console.log('overlayConfig', i);
            this[i] = overlayConfig[i];
          }
        }
      }
    }
  }

  protected isVisibleInZoom(zoom) {
    if (!this.isVisible) {
      return false;
    }
    if (this.min_zoom && this.min_zoom > zoom) {
      return false;
    }
    if (this.max_zoom && this.max_zoom < zoom) {
      return false;
    }
    return true;
  }

  public forEachEntity(cb) {
    this.entityIds.forEach((id) => {
      cb(id);
    });
  }

  public showOverlayById(id){
    (this.isVisibleInZoom(this.currentZoomLevel)) ? this.view.showOverlay(id) : '';
  }
}


